const QUEUE_STORAGE_KEY = 'sc-offline-queue-v2';
const QUEUE_EVENT = 'service-companion:queue-change';

export interface QueuedMutation {
  id: string;
  path: string;
  method: 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  description: string;
  createdAt: string;
}

function emitQueueChange(): void {
  window.dispatchEvent(new CustomEvent(QUEUE_EVENT));
}

export function loadQueuedMutations(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedMutation[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueuedMutations(queue: QueuedMutation[]): void {
  localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  emitQueueChange();
}

export function enqueueMutation(input: Omit<QueuedMutation, 'id' | 'createdAt'>): QueuedMutation {
  const item: QueuedMutation = {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    ...input
  };
  const queue = loadQueuedMutations();
  queue.push(item);
  saveQueuedMutations(queue);
  return item;
}

export function removeQueuedMutation(id: string): void {
  const queue = loadQueuedMutations().filter((item) => item.id !== id);
  saveQueuedMutations(queue);
}

export function getQueuedMutationCount(): number {
  return loadQueuedMutations().length;
}

export function subscribeQueueChanges(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(QUEUE_EVENT, handler);
  window.addEventListener('online', handler);
  return () => {
    window.removeEventListener(QUEUE_EVENT, handler);
    window.removeEventListener('online', handler);
  };
}

export async function flushQueuedMutations(baseUrl: string): Promise<{ flushed: number; failed: number }> {
  const queue = loadQueuedMutations();
  if (queue.length === 0) return { flushed: 0, failed: 0 };
  if (baseUrl.startsWith('local://')) {
    saveQueuedMutations([]);
    return { flushed: queue.length, failed: 0 };
  }

  let flushed = 0;
  let failed = 0;
  const nextQueue: QueuedMutation[] = [];
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  const slowConnection = Boolean(connection?.saveData) || ['slow-2g', '2g'].includes(connection?.effectiveType ?? '');
  const concurrency = slowConnection ? 1 : 3;

  for (let index = 0; index < queue.length; index += concurrency) {
    const slice = queue.slice(index, index + concurrency);
    const results = await Promise.all(
      slice.map(async (item) => {
        try {
          const init: RequestInit = { method: item.method };
          if (item.body) {
            init.headers = { 'content-type': 'application/json' };
            init.body = JSON.stringify(item.body);
          }
          const response = await fetch(`${baseUrl}${item.path}`, init);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return { ok: true as const, item };
        } catch (error) {
          return { ok: false as const, item, error };
        }
      })
    );

    for (const result of results) {
      if (result.ok) {
        flushed += 1;
      } else {
        failed += 1;
        nextQueue.push(result.item);
      }
    }
  }

  saveQueuedMutations(nextQueue);
  return { flushed, failed };
}
